import React, { useState, useEffect, useCallback } from 'react';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Legend,
} from 'recharts';
import AdminLayout, { adminFetch } from './AdminLayout';

const RANGES = ['7d', '30d', '90d'];

// Palette pour les statuts (cohérent avec dossier state machine)
const STATUS_COLORS = {
  brouillon: '#D1CCC2',
  en_cours: '#A0763E',
  envoyé: '#5A85B8',
  accepté: '#7A9E6E',
  refusé: '#C03434',
  abandonné: '#888888',
};

export default function AdminAnalytics() {
  const [range, setRange] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (r) => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminFetch(`/api/admin/analytics/dashboard?range=${r}`);
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError(e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(range); }, [range, fetchData]);

  return (
    <AdminLayout title="Analytics">
      <div style={{ padding: 24 }}>
        <h2 style={{ color: '#5A3C1F', marginBottom: 16 }}>Tableau de bord analytique</h2>

        {/* Range selector */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24, alignItems: 'center' }}>
          <span style={{ color: '#6B5847', fontSize: 14 }}>Période :</span>
          {RANGES.map(r => (
            <button
              key={r}
              onClick={() => setRange(r)}
              disabled={loading}
              style={{
                padding: '8px 16px',
                background: range === r ? '#A0763E' : 'transparent',
                color: range === r ? '#fff' : '#5A3C1F',
                border: `1px solid ${range === r ? '#A0763E' : '#D1CCC2'}`,
                borderRadius: 6,
                cursor: loading ? 'wait' : 'pointer',
                fontWeight: 600,
              }}
            >
              {r === '7d' ? '7 jours' : r === '30d' ? '30 jours' : '90 jours'}
            </button>
          ))}
          {loading && <span style={{ color: '#6B5847', fontSize: 14, marginLeft: 12 }}>Chargement…</span>}
        </div>

        {error && (
          <div style={{ padding: 12, background: '#FEE', color: '#933', borderRadius: 8, marginBottom: 12 }}>
            {error}
          </div>
        )}

        {data && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
              {/* Conversion rate — big stat */}
              <Card title="Taux de conversion">
                <div style={{ textAlign: 'center', padding: 16 }}>
                  <div style={{ fontSize: 48, color: '#A0763E', fontWeight: 700 }}>
                    {(data.conversion.rate * 100).toFixed(1)}%
                  </div>
                  <div style={{ color: '#6B5847', fontSize: 14, marginTop: 8 }}>
                    {data.conversion.dossierCount} dossier(s) créés sur {data.conversion.matchingCount} matching(s)
                  </div>
                </div>
              </Card>

              {/* Dossiers par statut — pie chart */}
              <Card title="Dossiers par statut">
                {data.dossiersByStatus.length === 0 ? (
                  <Empty />
                ) : (
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie
                        data={data.dossiersByStatus}
                        dataKey="count"
                        nameKey="statut"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={({ statut, count }) => `${statut} (${count})`}
                      >
                        {data.dossiersByStatus.map((entry, i) => (
                          <Cell key={i} fill={STATUS_COLORS[entry.statut] || '#888'} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </Card>

              {/* Top agents — list */}
              <Card title="Top agents (par dossiers)">
                {data.topAgents.length === 0 ? (
                  <Empty />
                ) : (
                  <ol style={{ paddingLeft: 24, color: '#5A3C1F' }}>
                    {data.topAgents.map(a => (
                      <li key={a.agentId} style={{ padding: '8px 0', borderBottom: '1px solid #F0EDE6' }}>
                        <span style={{ fontFamily: 'monospace', fontSize: 13 }}>{a.agentId}</span>
                        <span style={{ float: 'right', fontWeight: 600 }}>{a.count} dossier(s)</span>
                      </li>
                    ))}
                  </ol>
                )}
              </Card>
            </div>

            {/* Matchings par jour — line chart sur toute la largeur */}
            <Card title="Matchings par jour" style={{ marginTop: 16 }}>
              {data.matchingsByDay.length === 0 ? (
                <Empty />
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={data.matchingsByDay}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E0DDD8" />
                    <XAxis dataKey="date" stroke="#6B5847" />
                    <YAxis stroke="#6B5847" />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="count" stroke="#A0763E" strokeWidth={2} name="Matchings" />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Card>

            {/* Footer info */}
            <div style={{ marginTop: 16, color: '#6B5847', fontSize: 12, textAlign: 'right' }}>
              Calculé le {new Date(data.computedAt).toLocaleString('fr-FR')} —
              Du {data.from.slice(0, 10)} au {data.to.slice(0, 10)}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function Card({ title, children, style = {} }) {
  return (
    <div style={{
      background: '#fff',
      borderRadius: 8,
      border: '1px solid #E0DDD8',
      padding: 16,
      ...style,
    }}>
      <h3 style={{ color: '#5A3C1F', margin: '0 0 12px 0', fontSize: 16 }}>{title}</h3>
      {children}
    </div>
  );
}

function Empty() {
  return <div style={{ padding: 24, textAlign: 'center', color: '#6B5847' }}>Aucune donnée sur cette période</div>;
}
